import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Filter, Loader, Search, FileText, CheckCircle, Settings, Eye, X, Trash2, Clock, RotateCw } from 'lucide-react';
import axios from 'axios';
import AssignmentCard from '../components/AssignmentComponents/AssignmentCard';
import AssignmentForm from '../components/AssignmentComponents/AssignmentForm';
import SubmissionModal from '../components/AssignmentComponents/SubmissionModal';
import ViewSubmissionsModal from '../components/AssignmentComponents/ViewSubmissionsModal';
import ViewQuizSubmissionsModal from '../components/AssignmentComponents/ViewQuizSubmissionsModal';
import TakeQuizModal from '../components/AssignmentComponents/TakeQuizModal';

const Assignments = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isLecturer = user?.role === 'lecturer' || user?.role === 'admin';

  // Shared state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('assignments'); // 'assignments' or 'quizzes'

  // Lecturer state - Assignments
  const [assignedUnits, setAssignedUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showViewSubmissions, setShowViewSubmissions] = useState(false);
  const [saving, setSaving] = useState(false);

  // Lecturer state - Quizzes
  const [quizzes, setQuizzes] = useState([]);
  const [quizModalVisible, setQuizModalVisible] = useState(false);
  const [quizTitle, setQuizTitle] = useState('');
  const [quizDescription, setQuizDescription] = useState('');
  const [quizDuration, setQuizDuration] = useState('');
  const [quizAttempts, setQuizAttempts] = useState('1');
  const [quizShuffleQuestions, setQuizShuffleQuestions] = useState(false);
  const [quizShowResults, setQuizShowResults] = useState(true);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [showViewQuizSubmissions, setShowViewQuizSubmissions] = useState(false);

  // Question Modal State
  const [questionModalVisible, setQuestionModalVisible] = useState(false);
  const [questionType, setQuestionType] = useState('multiple_choice');
  const [questionText, setQuestionText] = useState('');
  const [questionOptions, setQuestionOptions] = useState(['', '', '', '']);
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [questionPoints, setQuestionPoints] = useState('1');

  // Student state
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showTakeQuizModal, setShowTakeQuizModal] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState(null);

  useEffect(() => {
    if (isLecturer) {
      fetchAssignedUnits();
    } else {
      fetchStudentAssignments();
    }
  }, [user]);

  useEffect(() => {
    if (isLecturer && selectedUnit) {
      fetchLecturerAssignments();
      fetchLecturerQuizzes();
    }
  }, [selectedUnit]);

  // --- Fetch Logic ---

  const fetchAssignedUnits = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/lecturers/${user.id}/assigned-units`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      const acceptedUnits = response.data.filter(unit => unit.assignmentStatus === 'accepted' || unit.assignmentStatus === 'active');
      setAssignedUnits(acceptedUnits);

      if (acceptedUnits.length > 0 && !selectedUnit) {
        setSelectedUnit(acceptedUnits[0]);
      }
    } catch (err) {
      setError('Failed to load units.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLecturerAssignments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/assignments/unit/${selectedUnit.id}/lecturer`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setAssignments(response.data);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch assignments.');
    } finally {
      setLoading(false);
    }
  };

  const fetchLecturerQuizzes = async () => {
    // Only fetch if unit selected
    if (!selectedUnit) return;
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/quizzes/unit/${selectedUnit.id}/lecturer`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setQuizzes(response.data);
    } catch (err) {
      console.error(err);
      // Don't block whole page if quizzes fail, just log
    }
  };

  const fetchStudentAssignments = async () => {
    try {
      setLoading(true);
      const [assignmentsResponse, quizzesResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/assignments/student`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/quizzes/student`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      const assignmentsData = assignmentsResponse.data;
      const quizzesData = quizzesResponse.data;

      setAssignments(assignmentsData);
      setQuizzes(quizzesData);

      // Extract unique units for student from both assignments and quizzes
      const unitsMap = new Map();

      assignmentsData.forEach(a => {
        if (a.unit && !unitsMap.has(a.unit.id)) {
          unitsMap.set(a.unit.id, a.unit);
        }
      });

      quizzesData.forEach(q => {
        if (q.unit && !unitsMap.has(q.unit.id)) {
          unitsMap.set(q.unit.id, q.unit);
        }
      });

      const extractedUnits = Array.from(unitsMap.values());
      setAssignedUnits(extractedUnits);

      if (extractedUnits.length > 0 && !selectedUnit) {
        setSelectedUnit(extractedUnits[0]);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to fetch data.');
    } finally {
      setLoading(false);
    }
  };

  // --- Assignment Actions (Lecturer) ---

  const handleSaveAssignment = async (formData) => {
    setSaving(true);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/assignments${currentAssignment ? `/${currentAssignment.id}` : ''}`;
      const method = currentAssignment ? 'put' : 'post';

      await axios[method](url, formData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      setShowForm(false);
      setCurrentAssignment(null);
      fetchLecturerAssignments();
    } catch (err) {
      alert('Failed to save assignment: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAssignment = async (id) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/assignments/${id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      fetchLecturerAssignments();
    } catch (err) {
      alert('Failed to delete assignment.');
    }
  };

  // --- Quiz Actions (Lecturer) ---

  const createQuiz = async () => {
    if (!quizTitle.trim()) {
      alert('Please enter a quiz title.');
      return;
    }
    if (quizQuestions.length === 0) {
      alert('Please add at least one question.');
      return;
    }

    try {
      const quizData = {
        title: quizTitle.trim(),
        description: quizDescription.trim(),
        duration: quizDuration ? parseInt(quizDuration) : null,
        maxAttempts: parseInt(quizAttempts),
        shuffleQuestions: quizShuffleQuestions,
        showResults: quizShowResults,
        questions: quizQuestions,
        unitId: selectedUnit.id,
        lecturerId: user.id,
      };

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/quizzes`, quizData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      alert('Quiz created successfully!');
      setQuizModalVisible(false);
      resetQuizForm();
      fetchLecturerQuizzes();
    } catch (err) {
      console.error(err);
      alert('Failed to create quiz: ' + (err.response?.data?.message || err.message));
    }
  };

  const addQuestion = () => {
    if (!questionText.trim()) {
      alert('Please enter question text.');
      return;
    }
    if (questionType === 'multiple_choice' && questionOptions.filter(opt => opt.trim()).length < 2) {
      alert('Please provide at least 2 options.');
      return;
    }
    if (questionType === 'multiple_choice' && !correctAnswer.trim()) {
      alert('Please select the correct answer.');
      return;
    }

    const newQuestion = {
      type: questionType,
      text: questionText.trim(),
      options: questionType === 'multiple_choice' ? questionOptions.filter(opt => opt.trim()) : [],
      correctAnswer: questionType === 'multiple_choice' ? correctAnswer.trim() : '',
      points: parseInt(questionPoints) || 1,
    };

    setQuizQuestions([...quizQuestions, newQuestion]);
    resetQuestionForm();
    setQuestionModalVisible(false);
  };

  const resetQuizForm = () => {
    setQuizTitle('');
    setQuizDescription('');
    setQuizDuration('');
    setQuizAttempts('1');
    setQuizShuffleQuestions(false);
    setQuizShowResults(true);
    setQuizQuestions([]);
  };

  const resetQuestionForm = () => {
    setQuestionType('multiple_choice');
    setQuestionText('');
    setQuestionOptions(['', '', '', '']);
    setCorrectAnswer('');
    setQuestionPoints('1');
  };


  // --- Student Actions ---

  const handleSubmitAssignment = async ({ submissionText, submissionFile }) => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('assignmentId', currentAssignment.id);
      if (submissionText) formData.append('submissionText', submissionText);
      if (submissionFile) {
        formData.append('submissionFile', submissionFile);
      }

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/assignment-submissions`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setShowSubmissionModal(false);
      setCurrentAssignment(null);
      fetchStudentAssignments();
      alert('Assignment submitted successfully!');
    } catch (err) {
      alert('Failed to submit: ' + (err.response?.data?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  // --- Render Helpers ---

  const filteredAssignments = assignments.filter(a => {
    const matchesSearch = a.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesUnit = selectedUnit ? (a.unitId === selectedUnit.id) : true;
    return matchesSearch && matchesUnit;
  });

  const filteredQuizzes = quizzes.filter(q => {
    const matchesSearch = q.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesUnit = selectedUnit ? (q.unitId === selectedUnit.id) : true;
    return matchesSearch && matchesUnit;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Assignments & Quizzes</h1>
          <p className="text-gray-500">
            {isLecturer ? 'Manage your class assessments' : 'View and submit your tasks'}
          </p>
        </div>
        {isLecturer && (
          <button
            onClick={() => {
              if (activeTab === 'assignments') {
                setCurrentAssignment(null);
                setShowForm(true);
              } else {
                setQuizModalVisible(true);
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            <Plus size={20} /> New {activeTab === 'assignments' ? 'Assignment' : 'Quiz'}
          </button>
        )}
      </div>

      {/* Tab Selector - Visible to All */}
      <div className="bg-gray-100 p-1 rounded-lg inline-flex mb-6">
        <button
          onClick={() => setActiveTab('assignments')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'assignments' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
        >
          Assignments
        </button>
        <button
          onClick={() => setActiveTab('quizzes')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'quizzes' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
        >
          Quizzes
        </button>
      </div>

      <div className="space-y-6 mb-6">
        {/* Unit Selector */}
        {assignedUnits.length > 0 && (
          <div className="flex gap-4 overflow-x-auto pb-2">
            {assignedUnits.map(unit => (
              <button
                key={unit.id}
                onClick={() => setSelectedUnit(unit)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${selectedUnit?.id === unit.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
              >
                {unit.code} - {unit.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Search Bar */}
      <div className="flex gap-4 mb-6 mt-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder={`Search ${activeTab === 'assignments' ? 'assignments' : 'quizzes'}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>
      </div>

      {/* Content Area */}
      {loading ? (
        <div className="flex justify-center p-12">
          <Loader className="animate-spin text-blue-600" size={32} />
        </div>
      ) : error ? (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg text-center">
          {error}
          <button onClick={() => window.location.reload()} className="block mx-auto mt-2 text-sm underline">Retry</button>
        </div>
      ) : (
        <>
          {/* Lecturer View: Assignments or Quizzes */}
          {isLecturer && activeTab === 'assignments' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAssignments.length > 0 ? filteredAssignments.map(assignment => (
                <AssignmentCard
                  key={assignment.id}
                  assignment={assignment}
                  role={user.role}
                  onEdit={(a) => { setCurrentAssignment(a); setShowForm(true); }}
                  onDelete={handleDeleteAssignment}
                  onSubmit={(a) => { setCurrentAssignment(a); setShowSubmissionModal(true); }}
                  onView={(a) => { setCurrentAssignment(a); setShowViewSubmissions(true); }}
                />
              )) : (
                <div className="col-span-full text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                  <p className="text-gray-500">No assignments found for this unit.</p>
                </div>
              )}
            </div>
          )}

          {isLecturer && activeTab === 'quizzes' && (
            <div className="space-y-4">
              {filteredQuizzes.length > 0 ? filteredQuizzes.map(quiz => (
                <div key={quiz.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        {quiz.title}
                      </h3>
                      {quiz.description && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{quiz.description}</p>}
                      <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                        <span>⏱️ {quiz.duration ? `${quiz.duration} min` : 'No limit'}</span>
                        <span>🔄 {quiz.maxAttempts} attempts</span>
                        <span>❓ {quiz.questions?.length || 0} questions</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setActiveQuiz(quiz);
                          setShowViewQuizSubmissions(true);
                        }}
                        className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded-lg"
                        title="View Submission"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-50 rounded-lg" title="Settings">
                        <Settings className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                  <p className="text-gray-500">No quizzes found for this unit.</p>
                </div>
              )}
            </div>
          )}

          {/* Student View: Assignments */}
          {!isLecturer && activeTab === 'assignments' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAssignments.length > 0 ? filteredAssignments.map(assignment => (
                <AssignmentCard
                  key={assignment.id}
                  assignment={assignment}
                  role={user.role}
                  onEdit={(a) => { setCurrentAssignment(a); setShowForm(true); }}
                  onDelete={handleDeleteAssignment}
                  onSubmit={(a) => {
                    if (a.submitted) {
                      alert('You have already submitted this assignment.');
                      return;
                    }
                    setCurrentAssignment(a);
                    setShowSubmissionModal(true);
                  }}
                  onView={(a) => alert('View submissions feature coming soon!')}
                />
              )) : (
                <div className="col-span-full text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                  <p className="text-gray-500">No assignments found for this unit.</p>
                </div>
              )}
            </div>
          )}

          {/* Student View: Quizzes */}
          {!isLecturer && activeTab === 'quizzes' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredQuizzes.length > 0 ? filteredQuizzes.map(quiz => (
                <div key={quiz.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-large transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div className={`${quiz.attempted ? 'bg-blue-100' : 'bg-green-100'} p-3 rounded-lg`}>
                      <CheckCircle className={`w-6 h-6 ${quiz.attempted ? 'text-blue-600' : 'text-green-600'}`} />
                    </div>
                    <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                      {quiz.unit?.code}
                    </span>
                  </div>

                  <h3 className="font-bold text-lg text-gray-900 mb-2">{quiz.title}</h3>
                  {quiz.description && <p className="text-gray-600 text-sm mb-4 line-clamp-2">{quiz.description}</p>}

                  <div className="flex flex-col gap-2 text-sm text-gray-500 mb-6">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1"><Clock size={14} /> {quiz.duration ? `${quiz.duration} min` : 'No limit'}</span>
                      <span className="flex items-center gap-1"><RotateCw size={14} /> {quiz.maxAttempts} attempts</span>
                    </div>
                    {quiz.attempted && (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="font-bold text-blue-600">
                          Score: {quiz.score !== null ? quiz.score : 'Pending'} / {quiz.totalPoints}
                        </span>
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                          Attempt {quiz.lastAttempt}
                        </span>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => navigate(`/quiz/${quiz.id}`)}
                    className={`w-full py-2 rounded-lg font-medium transition-colors ${quiz.attempted
                      ? 'bg-white border border-blue-600 text-blue-600 hover:bg-blue-50'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                  >
                    {quiz.attempted ? 'View Results' : 'Start Quiz'}
                  </button>
                </div>
              )) : (
                <div className="col-span-full text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                  <p className="text-gray-500">No quizzes found for this unit.</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* --- Modals --- */}

      {/* Assignment modals */}
      {showForm && selectedUnit && (
        <AssignmentForm
          assignment={currentAssignment}
          unitId={selectedUnit.id}
          onSave={handleSaveAssignment}
          onCancel={() => { setShowForm(false); setCurrentAssignment(null); }}
          isSaving={saving}
        />
      )}

      {showSubmissionModal && currentAssignment && (
        <SubmissionModal
          assignment={currentAssignment}
          onSubmit={handleSubmitAssignment}
          onCancel={() => { setShowSubmissionModal(false); setCurrentAssignment(null); }}
          isSubmitting={submitting}
        />
      )}

      {showViewSubmissions && currentAssignment && (
        <ViewSubmissionsModal
          assignment={currentAssignment}
          onClose={() => { setShowViewSubmissions(false); setCurrentAssignment(null); }}
        />
      )}

      {showTakeQuizModal && activeQuiz && (
        <TakeQuizModal
          quiz={activeQuiz}
          onClose={() => { setShowTakeQuizModal(false); setActiveQuiz(null); }}
          onComplete={() => {
            setShowTakeQuizModal(false);
            setActiveQuiz(null);
            alert('Quiz submitted successfully!');
            fetchStudentAssignments(); // Refresh to show updated attempt status
          }}
        />
      )}

      {showViewQuizSubmissions && activeQuiz && (
        <ViewQuizSubmissionsModal
          quiz={activeQuiz}
          onClose={() => { setShowViewQuizSubmissions(false); setActiveQuiz(null); }}
        />
      )}

      {/* Quiz Creator Modal */}
      {quizModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Create Quiz</h3>
              <button onClick={() => setQuizModalVisible(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
                    value={quizTitle}
                    onChange={(e) => setQuizTitle(e.target.value)}
                    placeholder="Quiz title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
                    rows={3}
                    value={quizDescription}
                    onChange={(e) => setQuizDescription(e.target.value)}
                    placeholder="Optional description"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
                      value={quizDuration}
                      onChange={(e) => setQuizDuration(e.target.value)}
                      placeholder="60"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Attempts</label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
                      value={quizAttempts}
                      onChange={(e) => setQuizAttempts(e.target.value)}
                      placeholder="1"
                    />
                  </div>
                </div>

                {/* Questions List inside Quiz Modal */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-medium text-gray-900">Questions ({quizQuestions.length})</h4>
                    <button
                      onClick={() => setQuestionModalVisible(true)}
                      className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" /> Add Question
                    </button>
                  </div>

                  {quizQuestions.length > 0 ? (
                    <div className="space-y-2">
                      {quizQuestions.map((q, idx) => (
                        <div key={idx} className="bg-white border border-gray-200 rounded p-3 flex justify-between items-start">
                          <div>
                            <span className="font-medium text-sm text-gray-600">Q{idx + 1}: </span>
                            <span className="text-sm">{q.text}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{q.type}</span>
                            <button onClick={() => setQuizQuestions(quizQuestions.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-2">No questions added yet.</p>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <button onClick={() => setQuizModalVisible(false)} className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600">Cancel</button>
                  <button onClick={createQuiz} className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700">Create Quiz</button>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Question Modal */}
      {questionModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Question</h3>
              <button onClick={() => setQuestionModalVisible(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={questionType}
                  onChange={(e) => setQuestionType(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="multiple_choice">Multiple Choice</option>
                  <option value="true_false">True/False</option>
                  <option value="short_answer">Short Answer</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Question Text</label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
                  rows={2}
                  value={questionText}
                  onChange={(e) => setQuestionText(e.target.value)}
                  placeholder="Enter question"
                />
              </div>

              {questionType === 'multiple_choice' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Options</label>
                  {questionOptions.map((opt, idx) => (
                    <div key={idx} className="flex gap-2 mb-2">
                      <input
                        type="radio"
                        name="correct"
                        checked={correctAnswer === opt && opt !== ''}
                        onChange={() => setCorrectAnswer(opt)}
                        disabled={!opt}
                        className="mt-2"
                      />
                      <input
                        type="text"
                        value={opt}
                        onChange={(e) => {
                          const newOpts = [...questionOptions];
                          newOpts[idx] = e.target.value;
                          setQuestionOptions(newOpts);
                        }}
                        className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm outline-none focus:border-green-500"
                        placeholder={`Option ${idx + 1}`}
                      />
                    </div>
                  ))}
                </div>
              )}

              {questionType === 'true_false' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Correct Answer</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2">
                      <input type="radio" name="tf" checked={correctAnswer === 'true'} onChange={() => setCorrectAnswer('true')} /> True
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="tf" checked={correctAnswer === 'false'} onChange={() => setCorrectAnswer('false')} /> False
                    </label>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button onClick={() => setQuestionModalVisible(false)} className="flex-1 bg-gray-500 text-white py-2 rounded-lg">Cancel</button>
                <button onClick={addQuestion} className="flex-1 bg-green-600 text-white py-2 rounded-lg">Add</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Assignments;
